import { createBatchLoader } from '@/lib/batching/createBatchLoader'

import type { ProductPriceLineInputDto } from '@/gen/bff'
import type { BatchLoader, BatchLoaderConfig } from '@/lib/batching/createBatchLoader'
import type { ProductPriceResult } from './productPricesSelect'

export interface PriceLoaderKey {
  productId: string
  quantity: number
}

export type LoadedPrice = ProductPriceResult['priceData']

export type PriceBatchLoader = BatchLoader<PriceLoaderKey, LoadedPrice>

export interface PriceBatchLoaderConfig
  extends Pick<BatchLoaderConfig<PriceLoaderKey, LoadedPrice>, 'maxBatchSize' | 'batchWindowMs'> {
  /** Fetch and convert prices for a chunk of products. Injected so the loader stays request-agnostic. */
  fetchPrices: (products: ProductPriceLineInputDto[]) => Promise<ProductPriceResult[]>
}

// Normalize quantity to >= 1 so the response key matches the request key: callers always load with a
// normalized quantity (quantity || 1), but the BFF may echo back 0 / omit it. Without this, a quantity-1
// request whose response carries quantity 0 would never match and the price would resolve to null.
const keyOf = (key: PriceLoaderKey): string => `${key.productId}-${key.quantity || 1}`

/**
 * Batched price loader. Each `load(productId, quantity)` returns a promise; calls made within a short
 * window are de-duplicated, chunked, and sent as a few **bulk** `getPrices` requests rather than one
 * request per product. Pair it with a per-product React Query key so each product is cached
 * independently — adding products fetches only the new ones, and a quantity change fetches only that
 * product, while the network stays batched.
 */
export const createPriceBatchLoader = ({
  fetchPrices,
  maxBatchSize,
  batchWindowMs,
}: PriceBatchLoaderConfig): PriceBatchLoader =>
  createBatchLoader<PriceLoaderKey, LoadedPrice>({
    identify: keyOf,
    missing: () => null,
    maxBatchSize,
    batchWindowMs,
    fetchChunk: async (keys) => {
      const rows = await fetchPrices(keys)

      return new Map(
        rows.map((row) => [keyOf({ productId: row.productId ?? '', quantity: row.quantity ?? 0 }), row.priceData])
      )
    },
  })
